import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CredentialType } from '@/sections/asset/components/tabs/credentials/credentialForms';
import type { WizardCredentialData } from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';

import { CredentialEditorBody } from './CredentialEditorBody';

export interface CredentialConfigModalProps {
  open: boolean;
  onClose: () => void;
  bundle: WizardCredentialData;
  onChange: (next: WizardCredentialData) => void;
  isWebService: boolean;
  webAppUrl: string;
  disabledTypes?: Partial<Record<CredentialType, string>>;
  title: string;
}

/**
 * Nested shadcn Dialog that wraps CredentialEditorBody for configuring one
 * credential bundle (the Shared config, or a per-app Custom row). It renders its
 * OWN Radix Dialog instance so Escape closes only this modal — never a
 * surrounding parent dialog. Edits live-commit to the parent via `onChange`
 * (no internal secret state). DialogContent carries `z-[150]` so it layers above
 * the z-[100] wizard dialog. The overlay (scrim) is lifted to `z-[150]` too so it
 * stacks with this modal instead of relying on portal order, and its nested scrim
 * is lightened in both themes (`bg-black/20`, `dark:bg-black/30`) so it layers over
 * the wizard's existing dim rather than double-dimming the backdrop.
 */
export function CredentialConfigModal({
  open,
  onClose,
  bundle,
  onChange,
  isWebService,
  webAppUrl,
  disabledTypes,
  title,
}: CredentialConfigModalProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="z-[150] max-h-[85vh] overflow-y-auto"
        overlayClassName="z-[150] bg-black/20 dark:bg-black/30"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Configure authentication credentials for this application.
          </DialogDescription>
        </DialogHeader>
        <CredentialEditorBody
          data={bundle}
          onChange={onChange}
          isWebService={isWebService}
          webAppUrl={webAppUrl}
          disabledTypes={disabledTypes}
        />
        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
